import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { reportAnimation, type ReportInput } from '../api/client';

/**
 * アニメーションの通報モーダル。
 * 実装は DevPortal.tsx の PlanCompareModal を踏襲（Escape で閉じる・body スクロールロック等）。
 * 未ログインの場合はフォームを出さず、ログイン導線を表示する。
 */
interface ReportModalProps {
  open: boolean;
  onClose: () => void;
  animationId: string;
  loggedIn: boolean;
}

const CATEGORY_OPTIONS: { value: ReportInput['category']; label: string }[] = [
  { value: 'rights', label: '権利侵害' },
  { value: 'inappropriate', label: '不適切な内容' },
  { value: 'spam', label: 'スパム' },
  { value: 'other', label: 'その他' },
];

const DETAIL_MAX = 500;

export default function ReportModal({ open, onClose, animationId, loggedIn }: ReportModalProps) {
  const location = useLocation();
  const [category, setCategory] = useState<ReportInput['category']>('rights');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // 開き直すたびにフォームの状態をリセットする。
  useEffect(() => {
    if (!open) return;
    setCategory('rights');
    setDetail('');
    setError('');
    setSent(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    closeBtnRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await reportAnimation(animationId, { category, detail: detail.trim() || undefined });
      setSent(true);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="report-modal-title">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="report-modal-title" className="modal__title">
            アニメーションを通報
          </h3>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="閉じる"
            ref={closeBtnRef}
          >
            ×
          </button>
        </div>

        {!loggedIn ? (
          <>
            <p className="modal__description">通報にはログインが必要です。</p>
            <div className="modal__actions">
              <button type="button" className="btn" onClick={onClose}>
                キャンセル
              </button>
              <Link
                to="/login"
                state={{ from: `${location.pathname}${location.search}` }}
                className="btn btn--primary"
              >
                ログイン
              </Link>
            </div>
          </>
        ) : sent ? (
          <p className="auth-form__success">通報を受け付けました。ご協力ありがとうございます。</p>
        ) : (
          <>
            <p className="modal__description">
              権利侵害や不適切な内容など、コミュニティガイドラインに反する投稿をお知らせください。
            </p>
            <div className="form">
              <div className="field">
                <label htmlFor="report-category">カテゴリー</label>
                <select
                  id="report-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ReportInput['category'])}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="report-detail">
                  詳細（任意） ({detail.length}/{DETAIL_MAX})
                </label>
                <textarea
                  id="report-detail"
                  rows={4}
                  maxLength={DETAIL_MAX}
                  value={detail}
                  onChange={(e) => setDetail(e.target.value)}
                />
              </div>

              {error && <div className="auth-form__error">{error}</div>}

              <div className="modal__actions">
                <button type="button" className="btn" onClick={onClose} disabled={busy}>
                  キャンセル
                </button>
                <button type="button" className="btn btn--primary" onClick={handleSubmit} disabled={busy}>
                  {busy ? '送信中…' : '通報する'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
